import { getValueValidationMessage } from "@/features/data-entry/domains/dataEntry.validation";

export const MONTHS = [
  { key: "1", label: "Jan" },
  { key: "2", label: "Feb" },
  { key: "3", label: "Mar" },
  { key: "4", label: "Apr" },
  { key: "5", label: "May" },
  { key: "6", label: "Jun" },
  { key: "7", label: "Jul" },
  { key: "8", label: "Aug" },
  { key: "9", label: "Sep" },
  { key: "10", label: "Oct" },
  { key: "11", label: "Nov" },
  { key: "12", label: "Dec" },
];

export const isMonthlyMode = (targetFrequency) => targetFrequency === "monthly";

const isValidMonth = (value) =>
  Number.isInteger(value) && value >= 1 && value <= 12;

const buildCalendarMonthOptions = () =>
  MONTHS.map(({ key, label }) => ({
    key,
    label,
    month: Number(key),
    year: null,
  }));

export const buildReportingPeriodMonths = (reportingPeriod, fallbackYear) => {
  const startYear = Number(reportingPeriod?.startYear);
  const startMonth = Number(reportingPeriod?.startMonth);
  const endYear = Number(reportingPeriod?.endYear);
  const endMonth = Number(reportingPeriod?.endMonth);
  const parsedFallbackYear = Number.parseInt(fallbackYear, 10);

  const hasValidPeriod =
    Number.isInteger(startYear) &&
    Number.isInteger(endYear) &&
    isValidMonth(startMonth) &&
    isValidMonth(endMonth);

  if (!hasValidPeriod) {
    return buildCalendarMonthOptions().map((item) => ({
      ...item,
      year: Number.isFinite(parsedFallbackYear) ? parsedFallbackYear : null,
    }));
  }

  const monthCount = (endYear - startYear) * 12 + endMonth - startMonth + 1;
  if (monthCount !== MONTHS.length) {
    return buildCalendarMonthOptions().map((item) => ({
      ...item,
      year: Number.isFinite(parsedFallbackYear) ? parsedFallbackYear : null,
    }));
  }

  return MONTHS.map((_, index) => {
    const zeroBasedMonth = startMonth - 1 + index;
    const year = startYear + Math.floor(zeroBasedMonth / 12);
    const month = (zeroBasedMonth % 12) + 1;
    const label = MONTHS[month - 1]?.label || String(month);

    return {
      key: String(index + 1),
      label: `${label} ${year}`,
      month,
      year,
    };
  });
};

export const getMonthlyFillCount = (months) => {
  if (!months || typeof months !== "object") return 0;
  return MONTHS.filter(({ key }) => {
    const v = months[key];
    return v !== undefined && v !== null && v !== "";
  }).length;
};

export const buildEmptyMonthlyTargetData = () => ({
  months: Object.fromEntries(MONTHS.map(({ key }) => [key, ""])),
});

const buildMonthsFromMonthlyValues = (monthlyValues = [], monthOptions) => {
  const options =
    Array.isArray(monthOptions) && monthOptions.length > 0
      ? monthOptions
      : buildCalendarMonthOptions();
  const records = (Array.isArray(monthlyValues) ? monthlyValues : []).filter(
    (item) => item && typeof item === "object",
  );
  const valueByPeriod = new Map(
    records.map((item) => [
      `${Number(item.year)}-${Number(item.month)}`,
      item.value ?? "",
    ]),
  );
  const valueByMonth = new Map(
    records.map((item) => [Number(item.month), item.value ?? ""]),
  );

  return Object.fromEntries(
    options.map(({ key, year, month }) => {
      const periodKey = `${Number(year)}-${Number(month)}`;
      const value = Number.isFinite(Number(year))
        ? valueByPeriod.get(periodKey)
        : valueByMonth.get(Number(month));
      return [key, value ?? ""];
    }),
  );
};

const resolveMonthlyTargetMonths = (targetData = {}, monthOptions) => {
  if (targetData?.months && typeof targetData.months === "object") {
    return targetData.months;
  }
  return buildMonthsFromMonthlyValues(targetData?.monthlyValues, monthOptions);
};

const NON_NUMERIC_INPUT_TYPES = new Set([
  "selector",
  "simple",
  "simpleLongText",
]);

const hasValue = (value) =>
  value !== undefined && value !== null && String(value) !== "";

export const hasFilledTargetData = (targetData = {}) => {
  if (!targetData || typeof targetData !== "object") {
    return hasValue(targetData);
  }

  if (hasValue(targetData.value)) return true;

  if (
    targetData.months &&
    typeof targetData.months === "object" &&
    Object.values(targetData.months).some(hasValue)
  ) {
    return true;
  }

  if (
    Array.isArray(targetData.monthlyValues) &&
    targetData.monthlyValues.some((item) => hasValue(item?.value))
  ) {
    return true;
  }

  if (
    Array.isArray(targetData.subEntries) &&
    targetData.subEntries.some(hasFilledTargetData)
  ) {
    return true;
  }

  return false;
};

const isPercentageUnit = (unit) => {
  const normalized = String(unit ?? "").trim();
  return normalized === "%" || /percent(age)?/i.test(normalized);
};

const isPercentageTarget = (disclosure) =>
  isPercentageUnit(disclosure?.defaultUnit);

const isNegativeZeroValue = (value) => {
  if (!hasValue(value)) return false;
  if (Object.is(value, -0)) return true;

  const raw = String(value).trim();
  if (!raw.startsWith("-")) return false;

  return Object.is(Number(raw), -0);
};

const parseTargetNumber = (value) => {
  if (!hasValue(value)) return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

const isPercentageOutOfRange = (value) => {
  const number = parseTargetNumber(value);
  if (number === null) return false;
  return !((number >= -100 && number <= -1) || (number >= 0 && number <= 100));
};

const formatCalculatedNumber = (value) => {
  if (!Number.isFinite(value)) return "";
  return String(Number(value.toPrecision(15)));
};

const formatConvertedMonthlyNumber = (value) => {
  if (!Number.isFinite(value)) return "";
  return String(Number(value.toPrecision(15)));
};

const omitTargetValueFields = (targetData = {}) => {
  const rest = { ...targetData };
  delete rest.value;
  delete rest.months;
  delete rest.monthlyValues;
  return rest;
};

export const isNumericTargetInputType = (inputType = "") =>
  !NON_NUMERIC_INPUT_TYPES.has(inputType);

export const formatTargetDisplayValue = (value) => {
  if (!hasValue(value)) return "-";
  return String(value);
};

export const normalizeMonthlyTargetData = (months = {}) => ({
  months: Object.fromEntries(
    MONTHS.map(({ key }) => [key, months?.[key] ?? ""]),
  ),
});

const getLatestMonthlyValue = (months = {}) => {
  for (let index = MONTHS.length - 1; index >= 0; index -= 1) {
    const value = months?.[MONTHS[index].key];
    if (hasValue(value)) return value;
  }
  return "";
};

export const convertYearlyToMonthlyTargetData = (
  targetData = {},
  inputType = "",
  disclosure,
) => {
  const rest = omitTargetValueFields(targetData);
  const yearlyValue = targetData?.value ?? "";
  const monthValue = isNumericTargetInputType(inputType)
    ? hasValue(yearlyValue)
      ? isPercentageTarget(disclosure)
        ? String(yearlyValue)
        : formatConvertedMonthlyNumber(Number(yearlyValue) / 12)
      : ""
    : yearlyValue;

  return {
    ...rest,
    ...normalizeMonthlyTargetData(
      Object.fromEntries(MONTHS.map(({ key }) => [key, monthValue])),
    ),
  };
};

export const convertMonthlyToYearlyTargetData = (
  targetData = {},
  inputType = "",
  monthOptions,
  disclosure,
) => {
  const rest = omitTargetValueFields(targetData);
  const months = resolveMonthlyTargetMonths(targetData, monthOptions);
  const value = isPercentageTarget(disclosure)
    ? (months?.[MONTHS[0].key] ?? "")
    : isNumericTargetInputType(inputType)
      ? (() => {
          const numericValues = MONTHS.map(({ key }) => months?.[key])
            .filter(hasValue)
            .map(Number)
            .filter(Number.isFinite);
          if (numericValues.length === 0) return "";
          return formatCalculatedNumber(
            numericValues.reduce((sum, current) => sum + current, 0),
          );
        })()
      : getLatestMonthlyValue(months);

  return {
    ...rest,
    value,
  };
};

export const buildTargetDataForFrequency = ({
  targetData = {},
  disclosure,
  targetFrequency = "yearly",
  monthOptions,
} = {}) => {
  const inputType = disclosure?.inputType || "";
  const isMonthly = isMonthlyMode(targetFrequency);
  const months = resolveMonthlyTargetMonths(targetData, monthOptions);
  const hasMonthlyValues =
    months &&
    typeof months === "object" &&
    MONTHS.some(({ key }) => hasValue(months[key]));

  if (isMonthly) {
    if (hasMonthlyValues) {
      return {
        ...omitTargetValueFields(targetData),
        ...normalizeMonthlyTargetData(months),
      };
    }
    if (hasValue(targetData?.value)) {
      return convertYearlyToMonthlyTargetData(
        targetData,
        inputType,
        disclosure,
      );
    }
    return {
      ...omitTargetValueFields(targetData),
      ...normalizeMonthlyTargetData(months),
    };
  }

  if (hasMonthlyValues) {
    if (
      targetData?.monthlyValuesSource === "yearly" &&
      hasValue(targetData?.value)
    ) {
      return {
        ...omitTargetValueFields(targetData),
        value: formatCalculatedNumber(Number(targetData.value)),
      };
    }
    return convertMonthlyToYearlyTargetData(
      targetData,
      inputType,
      monthOptions,
      disclosure,
    );
  }

  if (hasValue(targetData?.value)) {
    return {
      ...omitTargetValueFields(targetData),
      value: targetData.value,
    };
  }

  return {
    ...omitTargetValueFields(targetData),
    ...buildEmptyTargetData(disclosure),
  };
};

/**
 * Resolve the required target fields for a disclosure.
 * Target settings always stores a single "value" field.
 */
export const resolveTargetFields = () => ["value"];

const NUMERIC_TARGET_INPUT_TYPES = new Set([
  "value",
  "valueUnit",
  "valueUnitDenominator",
  "subEntries",
  "yearValue",
  "yearValueUnit",
  "yearValueCategory",
  "yearLocationValueUnit",
]);

const FALLBACK_YEAR_KEY = "na";

export const resolveDisclosureId = (disclosure) =>
  disclosure?._id ?? disclosure?.id ?? disclosure?.disclosureId ?? "";

export const buildDisclosureYearKey = (disclosureId, year) => {
  const normalizedDisclosureId = String(disclosureId ?? "").trim();
  const normalizedYear =
    year === null || year === undefined || year === ""
      ? FALLBACK_YEAR_KEY
      : String(year).trim();
  return `${normalizedDisclosureId}_${normalizedYear}`;
};

/**
 * Whether a disclosure is computed and read-only for target setting.
 * Computed disclosures with formulaScope "code" are read-only.
 * Computed disclosures with formulaScope "subEntries" can have manual targets.
 */
export const isComputedReadOnly = (disclosure) => {
  if (!disclosure) return false;
  return (
    Boolean(disclosure.isCalculated) && disclosure.formulaScope !== "subEntries"
  );
};

/**
 * Build an empty targetData object for a disclosure.
 */
export const buildEmptyTargetData = (disclosure) => {
  const fields = resolveTargetFields(disclosure);
  const data = {};
  for (const field of fields) {
    data[field] = "";
  }
  return data;
};

export const hasInvalidNegativeZeroTargetValue = (
  targetData = {},
  inputType,
) => {
  if (!NUMERIC_TARGET_INPUT_TYPES.has(inputType || "")) return false;

  if (targetData?.months && typeof targetData.months === "object") {
    return MONTHS.some(({ key }) =>
      isNegativeZeroValue(targetData.months[key]),
    );
  }

  return isNegativeZeroValue(targetData?.value);
};

export const getTargetValueValidationMessage = (
  disclosure,
  targetData = {},
  inputType,
) => {
  const resolvedInputType = inputType || disclosure?.inputType || "";
  if (!NUMERIC_TARGET_INPUT_TYPES.has(resolvedInputType)) {
    return null;
  }

  if (hasInvalidNegativeZeroTargetValue(targetData, resolvedInputType)) {
    return targetData?.months && typeof targetData.months === "object"
      ? "Monthly target values cannot be -0."
      : "Target value cannot be -0.";
  }

  if (isPercentageTarget(disclosure)) {
    if (targetData?.months && typeof targetData.months === "object") {
      const invalidMonth = MONTHS.find(({ key }) =>
        isPercentageOutOfRange(targetData.months[key]),
      );
      if (invalidMonth) {
        return "Each monthly percentage target must be between -100 and -1, or between 0 and 100.";
      }
    }

    if (isPercentageOutOfRange(targetData?.value)) {
      return "Percentage target must be between -100 and -1, or between 0 and 100.";
    }
  }

  return getValueValidationMessage(targetData?.value, {
    validations: disclosure?.validations,
  });
};
